import { Helmet } from "react-helmet-async";

const SITE_NAME = "Code Arena";
const BASE_URL = "https://www.code-arena.in";

/**
 * SEO Meta component using react-helmet-async.
 *
 * @param {string} title       - Page title (will append site name)
 * @param {string} description - Meta description (max ~160 chars ideal)
 * @param {string} url         - Canonical URL for the page
 * @param {string} image       - OG/Twitter image URL
 * @param {string} keywords    - Meta keywords (comma separated)
 * @param {string} type        - OG type (website, article, etc.)
 * @param {object} jsonLd      - Optional JSON-LD structured data object
 */
export default function SeoMeta({
  title,
  description,
  url,
  image,
  keywords,
  type = "website",
  jsonLd,
}) {
  const fullTitle = title?.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const canonicalUrl = url?.startsWith("http") ? url : `${BASE_URL}${url || ""}`;
  const ogImage = image?.startsWith("http") ? image : `${BASE_URL}${image || "/logo192.png"}`;

  return (
    <Helmet>
      {/* Primary Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
